// The overall result group, confirmed in "Result", beside the Test Result it is saved as
// (TGCLOUD-1868, plan B1, D3).
//
// The conclusion the reader read with confidence fills the Test Result with the group's own label,
// and one action confirms it. One it was not confident of leaves the Test Result EMPTY, with no
// default, and the card says so prominently: the clinician reads the conclusion where the report
// prints it and chooses the Test result. What they choose is then compared with what was read. A
// Test Result the Test held before the report (one set when the test was requested, or from an
// earlier report) is emptied too, and said here, "This Test held: Pending": an amended report is
// exactly where the result changes, so it is chosen again, never confirmed as it stood.
//
// "READ IT (HIGHLIGHTED)" (plan D3) is made true: when the card first says it, the conclusion is
// selected in the report (useVerificationView.highlight: no tab switched, nothing counted). Where
// the conclusion has no place to highlight, or the report cannot be shown, the sentence says only
// to read it in the report (review of the view).
import React from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { FIELD_LABELS } from 'src/components/pedigree-chart/pedigree-sidebar/sections/test-results/applyProposal';
import AnchorMark from './AnchorMark';
import GroupCard from './GroupCard';
import { ReadingComparison } from './LowConfidenceField';
import { useReadingChecksContext } from './useReadingChecks';
import { useVerification } from './useVerificationView';
import { fieldTarget } from './verificationTargets';
import {
  CONCLUSION_NOT_READ,
  CONCLUSION_NOT_READ_UNPLACED,
  CONFIRM_LABELS,
  GROUP_KIND_TEXT,
  heldBeforeText,
  lowConfidenceReasonText,
  notFilledInText
} from './labReportVerificationVocabulary';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// The reading's path for the Test result its conclusion suggests.
export const OVERALL_RESULT_PATH = 'testResultSuggestion';
// The conclusion as printed, which the card points to when it could not be read as a result.
const CONCLUSION_TARGET = fieldTarget('resultSummaryAsPrinted');

/**
 * @param {object} props
 * @param {string} props.testResult   the Test Result the form holds now
 */
export default function OverallResultCard({ testResult }) {
  const t = useAutoTranslation();

  const checks = useReadingChecksContext();
  const view = useVerification();
  const group = checks && checks.active ? checks.overallResultGroup : null;
  const doubted = !!group && group.confident !== true;
  // Where the conclusion is printed, to highlight: "Read it (highlighted)" is said only when it is.
  const conclusion =
    doubted && view?.anchorStateOf
      ? view.anchorStateOf(CONCLUSION_TARGET)
      : null;
  const placed = conclusion?.state === 'anchored';
  const reviewKey = checks?.review?.key || null;
  const pointed = React.useRef(null);
  React.useEffect(() => {
    // Once per review: the report shows the conclusion when the card first says to read it.
    if (!placed || !reviewKey || pointed.current === reviewKey) return;
    pointed.current = reviewKey;
    view.highlight?.(conclusion.paths);
    // `conclusion` is worked out on every render; the review and whether it is placed decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placed, reviewKey]);
  if (!group) return null;
  const low = checks.lowAt(OVERALL_RESULT_PATH);
  const empty = typeof testResult !== 'string' || testResult.trim() === '';
  // What the Test Result held before the report, emptied because the conclusion was not read with
  // confidence (applyProposal, `withheld`).
  const held = checks.heldBefore
    ? checks.heldBefore(OVERALL_RESULT_PATH)
    : null;
  return (
    <Box data-testid="overall-result-card">
      <GroupCard
        groupKey={group.key}
        title={t(GROUP_KIND_TEXT['overall-result'])}
        confirmLabel={t(CONFIRM_LABELS['overall-result'])}
      >
        {group.confident !== true && (
          <Alert
            severity="warning"
            data-testid="conclusion-not-read"
            sx={{ mb: 0.5 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t(placed ? CONCLUSION_NOT_READ : CONCLUSION_NOT_READ_UNPLACED)}
            </Typography>
            <AnchorMark
              target={CONCLUSION_TARGET}
              label={t(FIELD_LABELS.resultSummaryAsPrinted)}
              sx={{ ml: 0 }}
            />
          </Alert>
        )}
        <AnchorMark
          target={fieldTarget('testResult')}
          label={t(FIELD_LABELS.testResult)}
          sx={{ ml: 0 }}
        />
        {low && empty && (
          <Typography
            variant="caption"
            component="div"
            sx={{ color: 'text.secondary' }}
          >
            {notFilledInText(lowConfidenceReasonText(low.reason, t), t)}
          </Typography>
        )}
        {low && held && (
          <Typography
            variant="caption"
            component="div"
            data-testid="held-before"
            sx={{ color: 'text.secondary' }}
          >
            {heldBeforeText(held, t)}
          </Typography>
        )}
        {low && (
          <ReadingComparison path={OVERALL_RESULT_PATH} value={testResult} />
        )}
      </GroupCard>
    </Box>
  );
}
